import { useIsNarrowViewport } from "@/hooks/useIsNarrowViewport";
import { PrivateImage } from "@/pages/KnowledgeHubPage/creatives/PrivateImage";
import { ChevronDown } from "lucide-react";
import styles from "./CreativeContextSelector.module.css";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { CreativeUsageWarning } from "@/components/creative-materials/CreativeUsageWarning";
import {
  canAllowExternalUse,
  creativeUsageWarningLevel,
} from "@/components/creative-materials/creativeUsage";
import type { CreativeReferencePickerProps } from "@/types/creative-library";

/** Explicit per-chat references; the server also counts Board and product inputs. */
const MAX_EXPLICIT_REFERENCES = 4;

export function CreativeReferencePicker({
  items,
  roles,
  selected,
  disabled,
  intendedUse,
  onChange,
  primaryReferenceToken,
  isAlwaysOpen = false,
}: CreativeReferencePickerProps) {
  const { t } = useTranslation("app");
  const bodyId = useId();
  const [isToggled, setExpanded] = useState(false);
  const [wasOpened, setOpened] = useState(false);
  // Inside a section that already opened, a second toggle would be one click too many.
  const expanded = isAlwaysOpen || isToggled;
  const opened = isAlwaysOpen || wasOpened;
  // Order is fixed when the picker opens, so a tick never moves items under the cursor.
  const [selectedAtOpen] = useState(selected);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const scope = roles?.join(",") ?? "all";
  useEffect(() => {
    setPage(0);
    setQuery("");
  }, [scope]);
  const visible = items
    .filter((item) => !roles || roles.includes(item.role))
    .filter((item) =>
      item.title.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
    )
    .sort(
      (a, b) =>
        Number(selectedAtOpen.includes(b.token)) -
        Number(selectedAtOpen.includes(a.token)),
    );
  const pageSize = useIsNarrowViewport() ? 2 : 4;
  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const pageItems = visible.slice(
    currentPage * pageSize,
    currentPage * pageSize + pageSize,
  );
  const unavailable = selected.filter(
    (token) => !items.some((item) => item.token === token),
  );
  return (
    <div
      className="flex min-w-0 flex-col gap-2"
      data-testid="thread-creative-reference-picker"
    >
      {!isAlwaysOpen && (
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-expanded={expanded}
        aria-controls={bodyId}
        data-testid="thread-creative-references-toggle"
        onClick={() => {
          setOpened(true);
          setExpanded(!expanded);
        }}
      >
        <Text size="xs">{t("creativeLibrary.pinReferences")}</Text>
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          {t("creativeLibrary.pinnedCount", { count: selected.length })}
          <ChevronDown
            className={`h-4 w-4 transition-transform motion-reduce:transition-none ${expanded ? "rotate-180" : ""}`}
            aria-hidden
          />
        </span>
      </button>
      )}
      <div
        id={bodyId}
        data-expanded={expanded}
        aria-hidden={!expanded}
        {...(!expanded ? { inert: "" } : {})}
        className={styles.reveal}
      >
        <div className={styles.revealInner}>
          {opened && (
            <div className="flex flex-col gap-2 pt-2">
              <Text size="xs" color="muted">
                {t("creativeLibrary.pinReferencesHint")}
              </Text>
              <Input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPage(0);
                }}
                placeholder={t("creativeLibrary.searchReferences")}
                aria-label={t("creativeLibrary.searchReferences")}
                className="h-9"
                disabled={disabled}
              />
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                {pageItems.map((item) => {
                  const checked = selected.includes(item.token);
                  return (
                    <label
                      key={item.token}
                      className={`flex min-w-0 cursor-pointer flex-col gap-2 rounded-xl border bg-background p-2 text-sm transition-colors motion-reduce:transition-none ${checked ? "border-primary ring-1 ring-primary/20" : "border-border hover:border-primary/40"}`}
                    >
                      <PrivateImage
                        source={item.mediaUrl}
                        alt={item.title}
                        className="h-24 w-full rounded-lg object-contain"
                      />
                      <span className="flex min-w-0 items-start gap-2">
                        <Checkbox
                          checked={checked}
                          disabled={
                            disabled ||
                            (!checked &&
                              selected.length >= MAX_EXPLICIT_REFERENCES)
                          }
                          onCheckedChange={(value) =>
                            onChange(
                              value === true
                                ? [...selected, item.token]
                                : selected.filter(
                                    (token) => token !== item.token,
                                  ),
                            )
                          }
                          data-testid={`thread-creative-reference-${item.token}`}
                        />
                        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <span className="truncate" title={item.title}>
                            {item.title}
                          </span>
                          {item.token === primaryReferenceToken && (
                            <span className="text-xs text-primary">
                              {t("creativeLibrary.mainReference")}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {t(`creativeLibrary.roles.${item.role}`)} ·{" "}
                            {t(`creativeLibrary.usage.${item.usage}`)}
                          </span>
                        </span>
                      </span>
                    </label>
                  );
                })}
                {visible.length === 0 && (
                  <Text size="xs" color="muted" className="py-3">
                    {t("creativeLibrary.noMatches")}
                  </Text>
                )}
              </div>
              {pageCount > 1 && (
                <div className="flex items-center justify-between gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={currentPage === 0}
                    onClick={() => setPage(currentPage - 1)}
                  >
                    {t("creativeLibrary.previousPage")}
                  </Button>
                  <Text size="xs" color="muted" aria-live="polite">
                    {t("creativeLibrary.imagePage", {
                      page: currentPage + 1,
                      total: pageCount,
                    })}
                  </Text>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={currentPage + 1 >= pageCount}
                    onClick={() => setPage(currentPage + 1)}
                  >
                    {t("creativeLibrary.nextPage")}
                  </Button>
                </div>
              )}
              {unavailable.map((token) => (
                <div
                  key={token}
                  className="flex items-center justify-between gap-2 text-xs text-muted-foreground"
                >
                  <span>{t("creativeLibrary.unavailableSelection")}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={disabled}
                    onClick={() =>
                      onChange(selected.filter((value) => value !== token))
                    }
                    data-testid={`thread-creative-reference-remove-${token}`}
                  >
                    {t("creativeLibrary.remove")}
                  </Button>
                </div>
              ))}
              <Text size="xs" color="muted">
                {t("creativeLibrary.referenceBudgetHint")}
              </Text>
              {intendedUse === "external" &&
                !isAlwaysOpen &&
                items
                  .filter((item) => selected.includes(item.token))
                  .flatMap((item) => {
                    const usage = creativeUsageWarningLevel(item);
                    return usage
                      ? [
                          <CreativeUsageWarning
                            key={item.token}
                            name={item.title}
                            usage={usage}
                            allowedUseToken={
                              canAllowExternalUse(item) ? item.token : null
                            }
                            testId={`thread-creative-reference-usage-${item.token}`}
                          />,
                        ]
                      : [];
                  })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
